/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useCallback, useMemo, useRef, useState } from 'react';

type UseTextFieldErrorReturn = {
    hasTextError: boolean;
    makeErrorHandler: (field: string) => (hasError: boolean) => void;
    resetErrors: () => void;
};

export function useTextFieldError(): UseTextFieldErrorReturn {
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    // One stable handler reference per field name, persisted across renders.
    const handlersRef = useRef<Map<string, (hasError: boolean) => void>>(new Map());

    const hasTextError = useMemo(() => Object.values(errors).some(Boolean), [errors]);

    const makeErrorHandler = useCallback((field: string) => {
        const cached = handlersRef.current.get(field);
        if (cached) return cached;

        const handler = (hasError: boolean) => {
            setErrors((prev) => {
                // No-op when the value is unchanged – prevents unnecessary re-renders.
                if (prev[field] === hasError) return prev;
                return { ...prev, [field]: hasError };
            });
        };

        handlersRef.current.set(field, handler);
        return handler;
    }, []);

    const resetErrors = useCallback(() => {
        setErrors({});
        handlersRef.current.clear();
    }, []);

    return { hasTextError, makeErrorHandler, resetErrors };
}
